import {
  MCP_WRITE_APPROVAL_REASON_LABELS,
  MCP_WRITE_TOOL_LABELS,
  isMcpWriteToolName,
  type McpWriteCoverage,
  type McpWriteCoverageBasis,
  type McpWriteIntentView,
} from "@shared/mcp/writeIntents";

/**
 * The approve page's words (ADR 0009 section 6, release 8B): what the AI app asks to do, why it
 * needs the person, how the tables were counted, and what became of it. Typed by the closed
 * sets in `shared/mcp/writeIntents.ts`, so a new basis or reason does not compile without its
 * sentence.
 */

/** "Start a run": the shared fixed sentence, or the tool's own name for one the list lacks. */
export function writeToolLabel(intent: Pick<McpWriteIntentView, "tool" | "toolLabel">): string {
  if (intent.toolLabel) return intent.toolLabel;
  return isMcpWriteToolName(intent.tool) ? MCP_WRITE_TOOL_LABELS[intent.tool] : intent.tool;
}

export function writeReasonSentence(intent: Pick<McpWriteIntentView, "reason">): string | null {
  return intent.reason ? MCP_WRITE_APPROVAL_REASON_LABELS[intent.reason] : null;
}

const COVERAGE_BASIS_WORDS: Record<McpWriteCoverageBasis, string> = {
  none: "this change starts no import",
  entity_names: "the tables the AI app named",
  selected_tables: "the tables selected on the connection",
  schedule_members: "the selected tables of every enabled connection on the schedule",
  run_scope: "the tables the run covered",
  retryable_tables: "the tables of the run that can be retried",
  projected_selection: "the schedule's tables after this change",
};

/** "34 tables (the selected tables of every enabled connection on the schedule)". */
export function coverageSentence(coverage: McpWriteCoverage): string {
  const count = coverage.tables === 1 ? "1 table" : `${coverage.tables} tables`;
  return `${count} (${COVERAGE_BASIS_WORDS[coverage.basis]})`;
}

/** What the page says an ended code means, in the person's words; null for none. */
const OUTCOME_CODE_SENTENCES: Readonly<Record<string, string>> = {
  MCP_GRANT_ENDED:
    "The AI app's connection has ended, or it can no longer make changes in this instance, so nothing ran.",
  MCP_TOOL_RETIRED: "This action is no longer available in skyData, so nothing ran.",
  NOTHING_TO_RETRY: "Every table had already landed, so there was nothing to retry.",
  CONNECTION_DISABLED: "The connection is disabled, so nothing ran.",
  // #1291: a retry_run refused because a dbt job of the run is disabled or was deleted.
  DBT_JOB_DISABLED: "A dbt job of the run is disabled or was deleted, so nothing ran.",
  REFRESH_PENDING: "A refresh of the warehouse catalog was already waiting.",
  INTERNAL: "skyData stopped before it could record how the change ended.",
};

export function outcomeCodeSentence(code: string | null): string | null {
  if (!code) return null;
  return OUTCOME_CODE_SENTENCES[code] ?? `The change did not run (${code}).`;
}

/** A summary value as text: a list joined, a yes or no, a dash for nothing. */
export function summaryValueText(value: string | number | boolean | null | string[]): string {
  if (value === null) return "-";
  if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

/**
 * Whether the approve link can still be used: waiting for the person and not past its 24 hours.
 * The server decides; this only keeps the buttons from offering what it would refuse.
 */
export function canDecide(intent: McpWriteIntentView, now: Date = new Date()): boolean {
  if (intent.status !== "awaiting_approval") return false;
  if (!intent.approvalExpiresAt) return true;
  return new Date(intent.approvalExpiresAt).getTime() > now.getTime();
}

/** The one state the page is in, from the intent and the clock. */
export type ApprovePhase = "decide" | "expired" | "running" | "done" | "failed" | "declined";

export function approvePhase(intent: McpWriteIntentView, now: Date = new Date()): ApprovePhase {
  switch (intent.status) {
    case "awaiting_approval":
      return canDecide(intent, now) ? "decide" : "expired";
    case "executing":
    case "committing":
    case "prepared":
      return "running";
    case "executed":
      return "done";
    case "failed":
      return "failed";
    case "declined":
      return "declined";
    case "expired":
      return "expired";
  }
}
